import { OakGoogleClassroomException } from "@/errors";
import { GoogleOAuthClient } from "@/services/GoogleOAuth";
import { Code, googleOAuthUserSchema, GoogleOAuthUser } from "@/types/services";

export type TokenValidationResult = {
  token: string;
  wasRefreshed: boolean;
};

export class OakOAuth {
  constructor(
    private readonly googleOAuthClientId: string,
    private readonly googleOAuthClientSecret: string,
    private readonly googleOAuthCallbackApiRoute: string,
  ) {}

  /**
   * Creates a new GoogleOAuthClient instance with the stored OAuth credentials.
   * @param accessToken - Optional access token to set on the client
   * @param refreshToken - Optional refresh token to set on the client
   */
  public createClient(
    accessToken?: string,
    refreshToken?: string,
  ): GoogleOAuthClient {
    return new GoogleOAuthClient(
      this.googleOAuthClientId,
      this.googleOAuthClientSecret,
      this.googleOAuthCallbackApiRoute,
      accessToken,
      refreshToken,
    );
  }

  /**
   * Generates the Google OAuth sign-in URL for user authentication.
   * @param loginHint - Optional login hint to attach to generated url
   * @param subscribeToNewsletter - Whether to add user to the mailing list
   * @param usePupilScopes - Whether to request scopes for a pupil or teacher account
   */
  public async getSignInUrl(
    loginHint?: string,
    subscribeToNewsletter: boolean = false,
    usePupilScopes: boolean = false,
  ): Promise<string> {
    const oauthClient = this.createClient();
    const url: string | null = oauthClient.getAuthenticateUrl(
      loginHint,
      subscribeToNewsletter,
      usePupilScopes,
    );
    if (!url)
      throw OakGoogleClassroomException.GoogleOAuthError(
        "Failed to get Google Authentication URL",
        "getSignInUrl",
      );
    return url;
  }

  /**
   * Exchanges a Google OAuth authorization code for user tokens and profile info.
   * @param code - The authorization code received from Google's OAuth callback
   */
  public async exchangeCodeForTokens(code: Code): Promise<GoogleOAuthUser> {
    if (!code || typeof code !== "string" || code.trim() === "") {
      throw OakGoogleClassroomException.GoogleOAuthError(
        "Invalid OAuth code provided",
        "exchangeCodeForTokens",
      );
    }

    const oauthClient = this.createClient();
    const user = await oauthClient.exchangeCodeForTokens(code);
    if (!user) {
      throw OakGoogleClassroomException.GoogleOAuthError(
        "Failed to authenticate Google sign-in",
        "exchangeCodeForTokens",
      );
    }

    const parsedUser = googleOAuthUserSchema.parse(user);

    const accessToken = parsedUser.accessToken;
    if (!accessToken) {
      throw OakGoogleClassroomException.GoogleOAuthError(
        "Access token missing from OAuth response",
        "exchangeCodeForTokens",
        { loginHint: parsedUser.loginHint },
      );
    }

    return parsedUser;
  }

  /**
   * Checks if an access token is still valid, and attempts to refresh it if expired.
   * Returns null if both the access token and refresh token are invalid.
   * @param accessToken - The current access token to validate
   * @param refreshToken - Optional refresh token to use if the access token has expired
   */
  public async validateOrRefreshToken(
    accessToken: string,
    refreshToken?: string,
  ): Promise<TokenValidationResult | null> {
    const client = this.createClient(accessToken, refreshToken);

    if (await client.isAuthenticated(accessToken))
      return { token: accessToken, wasRefreshed: false };

    const refreshedToken = await client.refreshAccessToken(refreshToken);
    if (refreshedToken) return { token: refreshedToken, wasRefreshed: true };

    return null;
  }
}
